import React, { useState, useEffect, useRef } from 'react';
import './QuestInputLayout.css';
import QUEST_FEATURES from '../../config/questFeatures';
import LiveChronik from './LiveChronik';
import HausnummerReminder from './HausnummerReminder';
import QuestVideoCaptureAuto from './QuestVideoCaptureAuto'; // 🎯 AUTO: Web oder Native
import AICostDisplay from './AICostDisplay';
import { loadFavoriten, findSimilarStrasse } from '../../utils/favoritenHelper';
import { useTranslation } from '../../context/LanguageContext';

/**
 * Quest-optimiertes Layout für Paket-Eingang
 * Nur Erfassung, keine Ausgabe/Suche
 */
const QuestInputLayout = ({ user, pakete, onPackageSaved }) => {
  const { t } = useTranslation();
  const [scannedData, setScannedData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [recentPackages, setRecentPackages] = useState([]);
  const [favoriten, setFavoriten] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null); // ✅ Neu: Fehlermeldung für unvollständige Scans
  const [paketeHeuteCount, setPaketeHeuteCount] = useState(0); // ✅ Anzahl Pakete von heute
  const [cameraKey, setCameraKey] = useState(0); // ✅ Key für Kamera-Reset
  
  // ✅ Regalauswahl States
  const [selectedRegal, setSelectedRegal] = useState(null); // z.B. "A", "VORNE"
  const [showNumberPicker, setShowNumberPicker] = useState(false); // Zeige Nummern-Auswahl

  // ✅ Berechne Live-Chronik und Statistik aus übergebenen Paketen
  useEffect(() => {
    if (pakete && pakete.length > 0) {
      // Heute-Datum als UTC-Prefix (konsistent mit Statistics.js)
      const todayStr = new Date().toISOString().slice(0, 10);
      
      // Statistik: Pakete von heute zählen
      const paketeHeute = pakete.filter(p => p.datum?.startsWith(todayStr));
      
      // Live-Chronik: Letzte 10 Pakete insgesamt
      const letzte10 = [...pakete]
        .sort((a, b) => b.datum.localeCompare(a.datum)) // Neueste zuerst
        .slice(0, 10);
      
      console.log(`📦 ${letzte10.length} Pakete für Live-Chronik (aus ${pakete.length} gesamt)`);
      console.log(`📊 Heute erfasst: ${paketeHeute.length} Pakete`);
      
      setRecentPackages(letzte10);
      setPaketeHeuteCount(paketeHeute.length);
    }
  }, [pakete]);

  // Lade Favoriten beim Mount
  useEffect(() => {
    if (user?.uid) {
      loadFavoriten(user.uid)
        .then(favs => {
          setFavoriten(favs);
          console.log(`✅ ${favs.length} Favoriten geladen für Autocorrect`);
        })
        .catch(err => {
          console.error('Fehler beim Laden der Favoriten:', err);
        });
    }
  }, [user]);

  // Callback wenn Video-Capture Daten erkennt
  const handleDataScanned = (data) => {
    console.log('[Quest] Daten gescannt:', data);
    console.log('[Quest] Hausnummer:', data.hausnummer);
    
    // Prüfe Pflichtfelder
    const missingFields = [];
    if (!data.empfaenger) missingFields.push('Empfänger');
    if (!data.strasse) missingFields.push('Straße');
    if (!data.hausnummer) missingFields.push('Hausnummer');
    
    // ❌ Unvollständiger Scan → Zeige Fehlermeldung
    if (missingFields.length > 0) {
      // ✅ NICHT-BLOCKIEREND: Keine alert(), sondern visuelles Feedback
      console.warn(`⚠️ SCAN UNVOLLSTÄNDIG! Fehlende Felder: ${missingFields.join(', ')}`);
      
      // Zeige Fehlermeldung für 3 Sekunden
      setErrorMessage(`⚠️ Scan unvollständig!\nFehlende Felder: ${missingFields.join(', ')}`);
      setTimeout(() => setErrorMessage(null), 3000);
      
      // Haptic Feedback (kurz = Fehler)
      if (navigator.vibrate) {
        navigator.vibrate([200, 100, 200]); // Error pattern
      }
      
      // KEIN setScannedData → Regalplatz-Feld erscheint nicht
      return;
    }
    
    // ✅ FAVORITEN-AUTOCORRECT: Prüfe ob gescannte Straße korrigiert werden kann
    if (data.strasse && favoriten.length > 0) {
      const match = findSimilarStrasse(data.strasse, favoriten, 0.65); // 65% Ähnlichkeit
      
      if (match) {
        console.log(`🎯 Straßen-Autocorrect: "${data.strasse}" → "${match.strasse}"`);
        
        // Korrigiere Adresse mit Favoriten-Daten
        data.strasse = match.strasse;
        
        // Optional: PLZ/Ort auch übernehmen falls gescannt fehlt
        if (!data.plz && match.plz) {
          console.log(`📍 PLZ aus Favorit: ${match.plz}`);
          data.plz = match.plz;
        }
        if (!data.ort && match.ort) {
          console.log(`📍 Ort aus Favorit: ${match.ort}`);
          data.ort = match.ort;
        }
        
        // Haptic Feedback für Autocorrect
        if (navigator.vibrate) {
          navigator.vibrate([50, 30, 50, 30, 50]); // Triple short = Autocorrect
        }
      }
    }
    
    // Warnung bei niedriger Confidence (nur wenn Pflichtfelder da sind)
    // ✅ Bei niedriger Confidence trotzdem verwenden - User merkt es eh am Display
    if (data.confidence && data.confidence < 0.7) {
      console.warn(`⚠️ Niedrige Erkennungsqualität: ${Math.round(data.confidence * 100)}%`);
      // Trotzdem weitermachen - kein blockierendes confirm()
    }
    
    setScannedData(data);
  };

  // Paket speichern
  const handleSave = async () => {
    if (!scannedData) return;

    // Pflichtfelder prüfen
    if (!scannedData.empfaenger || !scannedData.strasse || !scannedData.hausnummer) {
      alert(t('errors.requiredFieldsMissing'));
      return;
    }

    // ✅ Regalplatz-Pflicht für Quest-Erfassung
    if (!scannedData.regalplatz || scannedData.regalplatz.trim() === '') {
      alert(`⚠️ ${t('errors.shelfLocationRequired')}`);
      return;
    }

    setIsProcessing(true);

    try {
      // ✅ Paket-Daten im DB-Schema vorbereiten
      const paketData = {
        // Pflichtfelder - DB-Schema kompatibel
        name: scannedData.empfaenger,
        streetAndNumber: `${scannedData.strasse || ''} ${scannedData.hausnummer || ''}`.trim(),
        postalAndCity: `${scannedData.plz || ''} ${scannedData.ort || ''}`.trim(),
        regalplatz: scannedData.regalplatz.toUpperCase(),
        
        // Optional - DB-Schema kompatibel
        trackingNumber: scannedData.trackingNummer || scannedData.leitcode || '',
        
        // Zusatzfelder (noch nicht im Standard-Schema, werden aber gespeichert)
        ...(scannedData.absender && { absender: scannedData.absender }),
        ...(scannedData.paketdienst && { paketdienst: scannedData.paketdienst }),
        ...(scannedData.gewicht && { gewicht: scannedData.gewicht }),
        ...(scannedData.paketGroesse && { paketGroesse: scannedData.paketGroesse }),
        ...(scannedData.paketFarbe && { paketFarbe: scannedData.paketFarbe }),
        
        // Metadaten
        datum: new Date().toISOString(), // DB verwendet "datum" für Timestamp
        erfasstMit: 'quest3',
        uploaded: false,
        
        // ✅ Token-Usage für Kosten-Tracking (nur bei Quest-Scans)
        ...(scannedData._tokenUsage && {
          aiCost: {
            promptTokens: scannedData._tokenUsage.promptTokens,
            completionTokens: scannedData._tokenUsage.completionTokens,
            totalTokens: scannedData._tokenUsage.totalTokens,
            costUSD: scannedData._tokenUsage.costUSD,
            model: 'gpt-4o-mini',
            timestamp: new Date().toISOString()
          }
        })
      };

      // Callback an Parent (AddPackageWithAI.js)
      if (onPackageSaved) {
        await onPackageSaved(paketData);
      }

      // Zu Recent Packages hinzufügen
      setRecentPackages(prev => [paketData, ...prev].slice(0, 10));

      // ✅ Statistik aktualisieren (heute +1)
      setPaketeHeuteCount(prev => prev + 1);

      // Hausnummer-Reminder anzeigen
      if (QUEST_FEATURES.ui.showHausnummerReminder) {
        setShowReminder(true);
      }

      // Haptic Feedback
      if (QUEST_FEATURES.ui.hapticFeedback && navigator.vibrate) {
        navigator.vibrate([100, 50, 100]); // Success pattern
      }

      // ✅ NICHT hier resetten - scannedData wird für HausnummerReminder gebraucht!
      // Reset passiert in handleReminderClose() oder handleNewScan()
      setIsProcessing(false);
    } catch (error) {
      console.error('[Quest] Fehler beim Speichern:', error);
      alert('Fehler beim Speichern: ' + error.message);
      
      // Error haptic
      if (navigator.vibrate) {
        navigator.vibrate(300);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Neuer Scan starten
  const handleNewScan = () => {
    console.log('[Quest] 🔄 Neuer Scan gestartet - Reset aller States');
    setScannedData(null);
    setShowReminder(false);
    setSelectedRegal(null);
    setShowNumberPicker(false);
    setErrorMessage(null); // ✅ Fehlermeldung zurücksetzen
    setIsProcessing(false); // ✅ Processing-State zurücksetzen
    setCameraKey(prev => prev + 1); // ✅ Kamera neu mounten
  };

  // Reminder geschlossen
  const handleReminderClose = () => {
    console.log('[Quest] ✅ Reminder geschlossen - Reset für neuen Scan');
    setScannedData(null);
    setShowReminder(false);
    setSelectedRegal(null);
    setShowNumberPicker(false);
    setErrorMessage(null); // ✅ Fehlermeldung zurücksetzen
    setIsProcessing(false); // ✅ Processing-State zurücksetzen
    setCameraKey(prev => prev + 1); // ✅ Kamera neu mounten
  };

  // Manuelle Eingabe ermöglichen
  const handleFieldChange = (field, value) => {
    setScannedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="quest-input-layout">
      {/* 🎯 Platform-aware: Web (Screenshot) oder Native (Kamera) */}
      <div style={{ position: 'relative' }}>
        <QuestVideoCaptureAuto 
          key={cameraKey}
          onDataScanned={handleDataScanned}
          isActive={true}
        />

        {/* Fehler-Overlay für unvollständige Scans */}
        {errorMessage && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(244, 67, 54, 0.95)', // Rot für Fehler
            color: 'white',
            padding: '32px 40px',
            borderRadius: '16px',
            zIndex: 30,
            fontSize: '32px',
            fontWeight: 'bold',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            backdropFilter: 'blur(10px)',
            whiteSpace: 'pre-line', // Mehrzeiliger Text
            minWidth: '60%',
            animation: 'fadeIn 0.3s ease'
          }}>
            {errorMessage}
          </div>
        )}

        {/* Gescannte Daten - NUR Text mit individuellem Hintergrund */}
        {scannedData && !showReminder && (
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            right: '10px',
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            {/* Mock-Warnung */}
            {scannedData._isMock && (
              <div style={{ 
                background: '#ff9800', 
                color: 'white', 
                padding: '8px 12px', 
                borderRadius: '6px',
                fontSize: '18px',
                fontWeight: 'bold',
                textAlign: 'center',
                alignSelf: 'center'
              }}>
                MOCK-DATEN
              </div>
            )}
            
            {/* Erkannte Daten - Jede Zeile einzeln mit Hintergrund */}
            <div style={{ 
              fontSize: '28px',
              color: '#4CAF50',
              fontWeight: 'bold',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px'
            }}>
              {scannedData.empfaenger && (
                <div style={{ 
                  display: 'inline-block',
                  background: 'rgba(0, 0, 0, 0.7)',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  alignSelf: 'flex-start'
                }}>
                  {scannedData.empfaenger}
                </div>
              )}
              {(scannedData.strasse || scannedData.hausnummer) && (
                <div style={{ 
                  display: 'inline-block',
                  background: 'rgba(0, 0, 0, 0.7)',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  alignSelf: 'flex-start'
                }}>
                  {scannedData.strasse || ''} {scannedData.hausnummer || ''}
                </div>
              )}
              {(scannedData.plz || scannedData.ort) && (
                <div style={{ 
                  display: 'inline-block',
                  background: 'rgba(0, 0, 0, 0.7)',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  alignSelf: 'flex-start'
                }}>
                  {scannedData.plz} {scannedData.ort}
                </div>
              )}
            </div>
            
            {/* Regalplatz-Anzeige mit "Anderen Regalplatz"-Button */}
            <div style={{ 
              marginTop: '8px',
              display: 'flex',
              gap: '12px',
              alignItems: 'center'
            }}>
              <div style={{ 
                display: 'inline-block',
                background: 'rgba(0, 0, 0, 0.7)',
                padding: '8px 12px',
                borderRadius: '6px'
              }}>
                <span style={{
                  fontSize: '16px',
                  color: 'white',
                  fontWeight: 'bold'
                }}>
                  📦 Regalplatz: {scannedData.regalplatz || selectedRegal || '(wählen)'}
                </span>
              </div>
              
              {/* Button: Anderen Regalplatz wählen */}
              {(scannedData.regalplatz || selectedRegal) && (
                <button
                  onClick={() => {
                    setSelectedRegal(null);
                    setShowNumberPicker(false);
                    handleFieldChange('regalplatz', '');
                  }}
                  style={{
                    fontSize: '16px',
                    padding: '8px 16px',
                    background: 'rgba(255, 152, 0, 0.9)',
                    color: 'white',
                    border: '2px solid white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                  }}
                >
                  � Anderen Regalplatz
                </button>
              )}
            </div>
            
            {/* Confidence Indicator */}
            {scannedData.confidence && (
              <div style={{ 
                display: 'inline-block',
                background: 'rgba(0, 0, 0, 0.7)',
                padding: '4px 12px',
                borderRadius: '6px',
                fontSize: '16px', 
                color: scannedData.confidence > 0.7 ? '#4CAF50' : '#ff9800',
                fontWeight: 'bold',
                alignSelf: 'flex-start'
              }}>
                KI-Konfidenz: {Math.round(scannedData.confidence * 100)}%
              </div>
            )}
          </div>
        )}
      </div>

      {/* ✅ REGALAUSWAHL - Um Video herum (ausblenden wenn Regalplatz komplett) */}
      {scannedData && !showReminder && !scannedData.regalplatz && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          zIndex: 25
        }}>
          {/* Regal-Buttons: Erste Zeile oben, zweite Zeile rechtsbündig */}
          {!showNumberPicker ? (
            <>
              {/* Erste Zeile: A-S (oben zentriert) */}
              <div style={{ 
                position: 'absolute',
                top: '12px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '12px',
                pointerEvents: 'auto',
                animation: 'slideInFromTop 1s ease-out'
              }}>
                {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'S'].map((regal, idx) => (
                  <button
                    key={regal}
                    onClick={() => {
                      setSelectedRegal(regal);
                      setShowNumberPicker(true);
                    }}
                    style={{
                      fontSize: '24px',
                      padding: '16px 24px',
                      background: selectedRegal === regal ? '#4CAF50' : 'rgba(0, 0, 0, 0.8)',
                      color: 'white',
                      border: '2px solid #4CAF50',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      minWidth: '80px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                      animation: `slideInFromTop 1s ease-out ${idx * 0.05}s backwards`
                    }}
                  >
                    {regal}
                  </button>
                ))}
              </div>

              {/* Zweite Zeile: HINTEN, KASSE, etc. (rechtsbündig) */}
              <div style={{ 
                position: 'absolute',
                top: '100px',
                right: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                pointerEvents: 'auto',
                animation: 'slideInFromTop 1s ease-out 0.6s backwards'
              }}>
                {['HINTEN', 'KASSE', 'VORLOTTO', 'VORNE', 'XY'].map((regal, idx) => (
                  <button
                    key={regal}
                    onClick={() => {
                      // 3+ Buchstaben → fertig
                      handleFieldChange('regalplatz', regal);
                      setSelectedRegal(regal);
                    }}
                    style={{
                      fontSize: '24px',
                      padding: '16px 24px',
                      background: selectedRegal === regal ? '#4CAF50' : 'rgba(0, 0, 0, 0.8)',
                      color: 'white',
                      border: '2px solid #4CAF50',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      minWidth: '120px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                      animation: `slideInFromTop 1s ease-out ${0.6 + idx * 0.08}s backwards`
                    }}
                  >
                    {regal}
                  </button>
                ))}
              </div>

              {/* CSS Animation für Einflug von oben */}
              <style>{`
                @keyframes slideInFromTop {
                  from {
                    transform: translateY(-100px);
                    opacity: 0;
                  }
                  to {
                    transform: translateY(0);
                    opacity: 1;
                  }
                }
              `}</style>
            </>
          ) : (
            /* Nummern-Picker (0-59) über ganzen Screen mit Animation */
            <>
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'grid',
                gridTemplateColumns: 'repeat(10, 1fr)',
                gridTemplateRows: 'repeat(7, minmax(60px, 80px))', // ✅ ALLE Rows gleich hoch (7 Reihen total)
                gap: '20px',
                padding: '20px',
                pointerEvents: 'auto'
              }}>
                {/* E+ Label oben rechts */}
                <div style={{
                  gridColumn: '8 / 10',
                  gridRow: '1',
                  fontSize: '20px',
                  background: 'rgba(0, 0, 0, 0.8)',
                  color: 'white',
                  border: '2px solid #4CAF50',
                  borderRadius: '12px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                  animation: 'fadeIn 0.5s ease-out',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {selectedRegal} + ?
                </div>
                
                {/* Zurück-Button daneben rechts */}
                <button
                  onClick={() => {
                    setShowNumberPicker(false);
                    setSelectedRegal(null);
                  }}
                  style={{
                    gridColumn: '10 / 11',
                    gridRow: '1',
                    fontSize: '18px',
                    background: 'rgba(102, 102, 102, 0.9)',
                    color: 'white',
                    border: '2px solid white',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                    animation: 'fadeIn 0.5s ease-out',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ← Zurück
                </button>
                
                {/* Nummern 0-59 (6 Reihen × 10 Spalten) */}
                {Array.from({ length: 60 }, (_, i) => i).map(num => (
                  <button
                    key={num}
                    onClick={(e) => {
                      // ✅ Hearthstone-Style Animation beim Klick
                      e.target.style.animation = 'cardSelect 0.4s ease-out';
                      
                      setTimeout(() => {
                        const regalplatz = `${selectedRegal}${num}`;
                        handleFieldChange('regalplatz', regalplatz);
                        setShowNumberPicker(false);
                        setSelectedRegal(null);
                      }, 200); // Wartet kurz für die Animation
                    }}
                    style={{
                      gridRow: Math.floor(num / 10) + 2, // Zeile 2-7
                      gridColumn: (num % 10) + 1, // Spalte 1-10
                      fontSize: '28px',
                      padding: '8px', // ✅ Noch flacher: 12px → 8px
                      background: 'rgba(76, 175, 80, 0.4)', // ✅ Blasser (0.85 → 0.4)
                      color: 'white',
                      border: '2px solid rgba(255, 255, 255, 0.6)',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                      transition: 'all 0.2s',
                      animation: `numberSlideIn 0.8s ease-out ${num * 0.015}s backwards`, // ✅ Animation mit Verzögerung
                      height: '100%', // ✅ Nutzt volle Grid-Cell-Höhe (max 80px)
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(76, 175, 80, 0.7)'; // ✅ Hover blasser
                      e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(76, 175, 80, 0.4)';
                      e.target.style.transform = 'scale(1)';
                    }}
                  >
                    {num}
                  </button>
                ))}
              </div>

              {/* CSS Animationen für Zahlen */}
              <style>{`
                @keyframes numberSlideIn {
                  from {
                    transform: scale(0.5) translateY(-50px);
                    opacity: 0;
                  }
                  to {
                    transform: scale(1) translateY(0);
                    opacity: 1;
                  }
                }
                
                @keyframes cardSelect {
                  0% {
                    transform: scale(1) translateZ(0) rotateY(0deg);
                  }
                  30% {
                    transform: scale(1.3) translateZ(80px) rotateY(-5deg);
                    box-shadow: 0 20px 40px rgba(76, 175, 80, 0.8);
                    filter: brightness(1.4);
                  }
                  60% {
                    transform: scale(1.15) translateZ(50px) rotateY(3deg);
                  }
                  100% {
                    transform: scale(0.9) translateZ(0) rotateY(0deg);
                    opacity: 0.5;
                  }
                }
                
                @keyframes fadeIn {
                  from {
                    opacity: 0;
                  }
                  to {
                    opacity: 1;
                  }
                }
              `}</style>
            </>
          )}
        </div>
      )}

      {/* Große Buttons UNTER dem Video */}
      {scannedData && !showReminder && (
        <div style={{
          display: 'flex',
          gap: '16px',
          padding: '16px',
          background: '#f0f0f0'
        }}>
          <button 
            onClick={handleSave}
            disabled={isProcessing || !scannedData.empfaenger || !scannedData.strasse || !scannedData.hausnummer}
            style={{ 
              flex: 1,
              fontSize: '28px',
              padding: '24px',
              background: isProcessing ? '#ccc' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: isProcessing ? 'wait' : 'pointer',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
            }}
          >
            {isProcessing ? 'SPEICHERT...' : 'SPEICHERN'}
          </button>
          
          <button 
            onClick={handleNewScan}
            disabled={isProcessing}
            style={{ 
              flex: 1,
              fontSize: '28px',
              padding: '24px',
              background: '#666',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
            }}
          >
            NEU SCANNEN
          </button>
        </div>
      )}
      
      {/* Zusatzinfos kompakt UNTER den Buttons */}
      {scannedData && !showReminder && (scannedData.regalplatz || scannedData.absender || scannedData.gewicht || scannedData.paketFarbe || scannedData.paketGroesse || scannedData.trackingNummer || scannedData.leitcode || scannedData.paketdienst) && (
        <div style={{
          padding: '12px 16px',
          background: 'white',
          fontSize: '14px',
          color: '#666'
        }}>
          {scannedData.regalplatz && (
            <div style={{ marginBottom: '6px' }}>
              📦 Regalplatz: <strong style={{ color: '#4CAF50' }}>{scannedData.regalplatz}</strong>
            </div>
          )}
          {scannedData.paketdienst && (
            <div style={{ marginBottom: '6px' }}>
              Paketdienst: <strong>{scannedData.paketdienst}</strong>
            </div>
          )}

          {scannedData.absender && (
            <div style={{ marginBottom: '6px' }}>
              Absender: <strong>{scannedData.absender}</strong>
            </div>
          )}

          {scannedData.gewicht && (
            <div style={{ marginBottom: '6px' }}>
              Gewicht: <strong>{scannedData.gewicht}</strong>
            </div>
          )}

          {scannedData.paketFarbe && (() => {
            // Deutsche Farbnamen zu CSS konvertieren
            const getCssColor = (farbe) => {
              const farbeLower = farbe.toLowerCase();
              const colorMap = {
                'weiß': '#FFFFFF',
                'weiss': '#FFFFFF',
                'white': '#FFFFFF',
                'schwarz': '#000000',
                'black': '#000000',
                'rot': '#FF0000',
                'red': '#FF0000',
                'blau': '#0000FF',
                'blue': '#0000FF',
                'grün': '#00FF00',
                'gruen': '#00FF00',
                'green': '#00FF00',
                'gelb': '#FFFF00',
                'yellow': '#FFFF00',
                'orange': '#FFA500',
                'braun': '#8B4513',
                'brown': '#8B4513',
                'grau': '#808080',
                'gray': '#808080',
                'grey': '#808080',
                'lila': '#800080',
                'purple': '#800080',
                'rosa': '#FFC0CB',
                'pink': '#FFC0CB'
              };
              return colorMap[farbeLower] || farbeLower;
            };

            return (
              <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                Farbe:
                <span style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: getCssColor(scannedData.paketFarbe),
                  border: scannedData.paketFarbe.toLowerCase() === 'weiß' || scannedData.paketFarbe.toLowerCase() === 'weiss' || scannedData.paketFarbe.toLowerCase() === 'white' 
                    ? '2px solid #333' 
                    : 'none',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}></span>
              </div>
            );
          })()}

          {scannedData.paketGroesse && (
            <div style={{ marginBottom: '6px' }}>
              Größe: <strong>{scannedData.paketGroesse}</strong>
            </div>
          )}

          {scannedData.trackingNummer && (
            <div style={{ marginBottom: '6px' }}>
              Tracking: <strong>{scannedData.trackingNummer}</strong>
            </div>
          )}

          {scannedData.leitcode && (
            <div>
              Leitcode: <strong>{scannedData.leitcode}</strong>
            </div>
          )}
        </div>
      )}

      {/* Hausnummer Reminder */}
      {showReminder && (
        <HausnummerReminder 
          hausnummer={scannedData?.hausnummer}
          onClose={handleReminderClose}
          onNext={handleNewScan}
        />
      )}

      {/* Live Chronik mit integrierter Statistik */}
      {QUEST_FEATURES.ui.showLiveChronik && !showReminder && (
        <LiveChronik 
          packages={recentPackages} 
          paketeHeuteCount={paketeHeuteCount} 
        />
      )}
    </div>
  );
};

export default QuestInputLayout;
